import {
  Box,
  Input,
  Skeleton,
  Stack,
  Text,
  Flex,
  Badge,
  Button,
  IconButton
} from '@chakra-ui/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { fetchAPI } from '@/api/fetch';
import { Avatar } from '@/components/ui/avatar';
import { apiUpdateAccountProfile, apiUpdateAgencyProfile } from '@/api/services';
import {
  AgencyPlan,
  getAgencyPlanColor,
  getAgencyPlanLabel,
  getAgencyUsageLabel,
  getAgencyUsageUnit
} from '@/types/enums';
import { PencilSimple, Camera } from '@phosphor-icons/react';
import { AccountRole } from '@/types/enums';
import { LogoUploadDialog } from '@/views/Profile/LogoUploadDialog';

interface AccountInformationProps {
  account: Account | null;
  setAccount: (account: Account) => void;
}

export default function AccountInformation({ account, setAccount }: AccountInformationProps) {
  const [accountSubmitError, setAccountSubmitError] = useState<boolean>(false);
  const [agencySubmitError, setAgencySubmitError] = useState<boolean>(false);
  const [isAccountEditMode, setIsAccountEditMode] = useState(false);
  const [isAgencyEditMode, setIsAgencyEditMode] = useState(false);
  const [isLogoUploadOpen, setIsLogoUploadOpen] = useState(false);

  const isAdmin = account?.role === AccountRole.ADMIN;

  interface AccountFormValues {
    name: string;
  }

  interface AgencyFormValues {
    companyName: string;
  }

  const accountForm = useForm<AccountFormValues>({
    defaultValues: {
      name: account?.name || ''
    }
  });

  const agencyForm = useForm<AgencyFormValues>({
    defaultValues: {
      companyName: account?.agency?.name || ''
    }
  });

  const handleAccountSubmit = async (formData: AccountFormValues) => {
    setAccountSubmitError(false);
    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiUpdateAccountProfile({
            payload: {
              name: formData.name
            }
          })
      });
      if (response.success && account) {
        setAccount({
          ...account,
          name: formData.name
        });
        setIsAccountEditMode(false);
      } else {
        console.error('Failed to update account profile');
        setAccountSubmitError(true);
      }
    } catch (error) {
      console.error('Failed to update account profile:', error);
      setAccountSubmitError(true);
    }
  };

  const handleAgencySubmit = async (formData: AgencyFormValues) => {
    setAgencySubmitError(false);
    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiUpdateAgencyProfile({
            payload: {
              name: formData.companyName
            }
          })
      });
      if (response.success && account) {
        setAccount({
          ...account,
          agency: {
            ...account.agency,
            name: formData.companyName
          }
        });
        setIsAgencyEditMode(false);
      } else {
        console.error('Failed to update agency profile');
        setAgencySubmitError(true);
      }
    } catch (error) {
      console.error('Failed to update agency profile:', error);
      setAgencySubmitError(true);
    }
  };

  const handleAccountEdit = () => {
    if (account) {
      setAccountSubmitError(false);
      accountForm.setValue('name', account.name);
      setIsAccountEditMode(true);
    }
  };

  const handleAgencyEdit = () => {
    if (account) {
      setAgencySubmitError(false);
      agencyForm.setValue('companyName', account.agency?.name || '');
      setIsAgencyEditMode(true);
    }
  };

  const handleAccountCancel = () => {
    accountForm.setValue('name', account?.name || '');
    setIsAccountEditMode(false);
  };

  const handleAgencyCancel = () => {
    agencyForm.setValue('companyName', account?.agency?.name || '');
    setIsAgencyEditMode(false);
  };

  const handleLogoUploadSuccess = (agency: Agency) => {
    if (account) {
      setAccount({
        ...account,
        agency: agency
      });
    }
  };

  const renderAccountSection = () => (
    <Box
      bg="background.secondary"
      p="24px"
      borderRadius="12px"
      border="1px solid"
      borderColor="neutrals.200"
      flex="1"
      transition="all 0.2s ease-in-out"
    >
      <Flex justify="space-between" align="center" mb="24px">
        <Text fontSize="20px" fontWeight="600" color="neutrals.900">
          Account
        </Text>
        {!isAccountEditMode && (
          <Button
            onClick={handleAccountEdit}
            variant="ghost"
            borderRadius="full"
            p="8px"
            color="neutrals.900"
            fontSize="14px"
            disabled={!account}
          >
            <PencilSimple size={16} />
          </Button>
        )}
      </Flex>
      <Stack direction="column" gap="24px">
        <Stack>
          <Text fontSize="16px" color="neutrals.700">
            Full Name
          </Text>
          {account ? (
            isAccountEditMode ? (
              <form id="updateAccountForm" onSubmit={accountForm.handleSubmit(handleAccountSubmit)}>
                <Stack gap="16px">
                  <Input
                    readOnly={accountForm.formState.isSubmitting}
                    defaultValue={account.name || ''}
                    size="lg"
                    placeholder="Enter your full name"
                    bg="white"
                    {...accountForm.register('name', {
                      required: 'Name is required'
                    })}
                  />
                </Stack>
              </form>
            ) : (
              <Text fontSize="18px" color="neutrals.900">
                {account.name}
              </Text>
            )
          ) : (
            <Skeleton h="28px" w="180px" />
          )}
        </Stack>

        <Stack>
          <Text fontSize="16px" color="neutrals.700">
            Email Address
          </Text>
          {account ? (
            <Text fontSize="18px" color="neutrals.900">
              {account.email}
            </Text>
          ) : (
            <Skeleton h="28px" w="220px" />
          )}
        </Stack>

        {isAccountEditMode && (
          <Flex gap="8px" justify="flex-end" mt="16px">
            <Button
              onClick={handleAccountCancel}
              variant="outline"
              border="1px solid"
              borderColor="neutrals.300"
              color="neutrals.900"
              borderRadius="8px"
              fontSize="14px"
              p="8px 12px"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="updateAccountForm"
              fontSize="14px"
              bg="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              loading={accountForm.formState.isSubmitting}
              p="8px 24px"
            >
              Save
            </Button>
          </Flex>
        )}
      </Stack>
      {accountSubmitError && (
        <Box
          color="error.700"
          fontSize="16px"
          bg="error.50"
          p="16px"
          borderRadius="12px"
          border="1px solid"
          borderColor="error.200"
          mt="16px"
        >
          Failed to update account profile. Please try again.
        </Box>
      )}
    </Box>
  );

  const renderAgencySection = () => (
    <Box
      bg="background.secondary"
      p="24px"
      borderRadius="12px"
      border="1px solid"
      borderColor="neutrals.200"
      flex="1"
      transition="all 0.2s ease-in-out"
    >
      <Flex justify="space-between" align="center" mb="24px">
        <Text fontSize="20px" fontWeight="600" color="neutrals.900">
          Company
        </Text>
        {!isAgencyEditMode && isAdmin && (
          <Button
            onClick={handleAgencyEdit}
            variant="ghost"
            borderRadius="full"
            p="8px"
            color="neutrals.900"
            fontSize="14px"
            disabled={!account}
          >
            <PencilSimple size={16} />
          </Button>
        )}
      </Flex>
      <Stack direction="column" gap="24px">
        {account ? (
          <Flex align="center" gap="16px">
            <Box position="relative">
              <Avatar
                size="2xl"
                w="120px"
                h="120px"
                src={account.agency?.logo_url}
                name={account.agency?.name}
                bg="primary.900"
                color="white"
                fontSize="24px"
              />
              {isAdmin && (
                <IconButton
                  aria-label="Upload logo"
                  onClick={() => setIsLogoUploadOpen(true)}
                  position="absolute"
                  bottom="0"
                  right="0"
                  borderRadius="full"
                  size="sm"
                  bg="white"
                  border="2px solid"
                  borderColor="neutrals.200"
                  color="neutrals.900"
                  _hover={{ bg: 'neutrals.50' }}
                >
                  <Camera size={16} />
                </IconButton>
              )}
            </Box>
          </Flex>
        ) : (
          <Skeleton h="80px" w="80px" borderRadius="full" />
        )}

        <Stack>
          <Text fontSize="16px" color="neutrals.700">
            Company Name
          </Text>
          {account ? (
            isAgencyEditMode ? (
              <form id="updateAgencyForm" onSubmit={agencyForm.handleSubmit(handleAgencySubmit)}>
                <Stack gap="16px">
                  <Input
                    readOnly={agencyForm.formState.isSubmitting}
                    defaultValue={account.agency?.name || ''}
                    size="lg"
                    placeholder="Enter your company name"
                    bg="white"
                    {...agencyForm.register('companyName', {
                      required: 'Company name is required'
                    })}
                  />
                </Stack>
              </form>
            ) : (
              <Text fontSize="18px" color="neutrals.900">
                {account.agency?.name}
              </Text>
            )
          ) : (
            <Skeleton h="28px" w="160px" />
          )}
        </Stack>

        <Stack>
          <Text fontSize="16px" color="neutrals.700" mb="8px">
            Current Plan
          </Text>
          {account ? (
            <>
              <Badge
                px="16px"
                py="8px"
                w="fit-content"
                fontSize="16px"
                color={getAgencyPlanColor(account.agency?.plan)}
                border="1px solid"
                borderColor={getAgencyPlanColor(account.agency?.plan)}
                borderRadius="12px"
                bg="background.secondary"
              >
                {getAgencyPlanLabel(account.agency?.plan)}
              </Badge>
              {account.agency?.plan === AgencyPlan.ONE_MONTH_TRIAL &&
                account.agency.trial_start_date &&
                account.agency.trial_expiration_date && (
                  <Stack mt="12px">
                    <Flex justify="space-between">
                      <Text fontSize="16px" color="neutrals.700">
                        Trial Start Date:
                      </Text>
                      <Text fontWeight="bold" color="neutrals.700">
                        {new Date(account.agency.trial_start_date * 1000).toLocaleDateString(
                          'en-US',
                          { year: 'numeric', month: 'short', day: 'numeric' }
                        )}
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontSize="16px" color="neutrals.700">
                        Trial Expiration Date:
                      </Text>
                      <Text fontWeight="bold" color="neutrals.700">
                        {new Date(account.agency.trial_expiration_date * 1000).toLocaleDateString(
                          'en-US',
                          { year: 'numeric', month: 'short', day: 'numeric' }
                        )}
                      </Text>
                    </Flex>
                  </Stack>
                )}
              {account.agency?.usages && account.agency.usages.length > 0 && (
                <Stack gap="16px" mt="24px">
                  <Text fontSize="18px" color="neutrals.900" mb="4px" fontWeight="bold">
                    Monthly Usage Overview
                    <Text fontSize="14px" color="neutrals.500" fontWeight="normal">
                      Usage resets on the 1st of each month.
                    </Text>
                  </Text>
                  {account.agency.usages.map((usage) => {
                    const exhausted = usage.usage >= usage.limit;
                    const isUnlimited = usage.limit < 0;
                    const percentage = isUnlimited
                      ? 0
                      : usage.limit === 0
                      ? usage.usage > 0
                        ? 100
                        : 0
                      : Math.min((usage.usage / usage.limit) * 100, 100);

                    return (
                      <Box key={usage.type}>
                        <Flex justify="space-between" align="center" mb="8px" fontSize="14px">
                          <Text color="neutrals.900" fontWeight="500">
                            {getAgencyUsageLabel(usage.type)}
                          </Text>
                          <Text color="neutrals.700">
                            {isUnlimited ? (
                              <>
                                {usage.usage.toLocaleString()}{' '}
                                <Text as="span" color="neutrals.500">
                                  (Unlimited)
                                </Text>
                              </>
                            ) : (
                              <>
                                <Text
                                  as="span"
                                  fontWeight={exhausted ? 'bold' : 'normal'}
                                  color={exhausted ? 'error.500' : 'neutrals.700'}
                                >
                                  {usage.usage.toLocaleString()}
                                </Text>
                                <Text as="span" color="neutrals.700">
                                  {' '}
                                  / {usage.limit.toLocaleString()}
                                </Text>
                              </>
                            )}{' '}
                            {getAgencyUsageUnit(usage.type)}
                          </Text>
                        </Flex>
                        {!isUnlimited && (
                          <Box
                            bg="neutrals.200"
                            borderRadius="full"
                            h="8px"
                            w="100%"
                            overflow="hidden"
                          >
                            <Box
                              bg={exhausted ? 'error.500' : 'primary.900'}
                              h="100%"
                              w={`${percentage}%`}
                              transition="width 0.3s ease"
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </>
          ) : (
            <Skeleton h="36px" w="120px" borderRadius="12px" />
          )}
        </Stack>

        {isAgencyEditMode && (
          <Flex gap="8px" justify="flex-end" mt="16px">
            <Button
              onClick={handleAgencyCancel}
              variant="outline"
              border="1px solid"
              borderColor="neutrals.300"
              color="neutrals.900"
              borderRadius="8px"
              fontSize="14px"
              p="8px 12px"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="updateAgencyForm"
              fontSize="14px"
              bg="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              loading={agencyForm.formState.isSubmitting}
              p="8px 24px"
            >
              Save
            </Button>
          </Flex>
        )}
      </Stack>
      {agencySubmitError && (
        <Box
          color="error.700"
          fontSize="16px"
          bg="error.50"
          p="16px"
          borderRadius="12px"
          border="1px solid"
          borderColor="error.200"
          mt="16px"
        >
          Failed to update company profile. Please try again.
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Stack direction="column" gap="32px">
        <Flex gap="24px" alignItems="center">
          {account ? (
            <Avatar size="xl" name={account.name} bg="primary.900" color="white" fontSize="24px" />
          ) : (
            <Skeleton w="80px" h="80px" borderRadius="full" />
          )}
          <Stack flex="1">
            {account ? (
              <Stack gap="0">
                <Text fontSize="24px" fontWeight="600" color="neutrals.900">
                  {account.name}
                </Text>
                <Text fontSize="16px" color="neutrals.700">
                  {account.email}
                </Text>
              </Stack>
            ) : (
              <Stack>
                <Skeleton h="32px" w="200px" />
                <Skeleton h="24px" w="250px" />
              </Stack>
            )}
          </Stack>
        </Flex>

        <Flex gap="24px" w="100%" flexWrap="wrap">
          {renderAccountSection()}
          {renderAgencySection()}
        </Flex>
      </Stack>

      <LogoUploadDialog
        open={isLogoUploadOpen}
        onClose={() => setIsLogoUploadOpen(false)}
        onSuccess={handleLogoUploadSuccess}
      />
    </>
  );
}
